import cardsTable from "./tables/dropfront/cards.json";
import level01 from "./tables/dropfront/level01.json";
import level02 from "./tables/dropfront/level02.json";
import level03 from "./tables/dropfront/level03.json";
import level04 from "./tables/dropfront/level04.json";
import level05 from "./tables/dropfront/level05.json";
import level06 from "./tables/dropfront/level06.json";
import level07 from "./tables/dropfront/level07.json";
import level08 from "./tables/dropfront/level08.json";
import level09 from "./tables/dropfront/level09.json";
import level10 from "./tables/dropfront/level10.json";
import viewTable from "./tables/presentation/dropfront-view.json";
import type { RigTables } from "../view/unit-rig";
import type { AttackFxTables } from "../vfx/attack-fx";

export type DropfrontCardId = "assault" | "shooter" | "engineer" | "barrier";

export type DropfrontTargetKind =
  | "swarm"
  | "hq"
  | "beacon"
  | "nest"
  | "depot"
  | "turret";

export type DropfrontCardRole =
  | "assault"
  | "shooter"
  | "engineer"
  | "barrier";

export type EnemyRoleId = "grunt" | "runner" | "brute" | "gunner";

export type DropfrontCardDef = {
  id: DropfrontCardId;
  nameKey: string;
  shortLabelKey: string;
  spriteId: string;
  cost: number;
  hp: number;
  damage: number;
  fireInterval: number;
  range: number;
  role: DropfrontCardRole;
  structureMul: number;
  canMove: boolean;
  priority: DropfrontTargetKind[];
  barrierHp?: number;
  barrierSynergyHpBonus?: number;
};

export type EnemyRoleDef = {
  moveInterval: number;
  hp: number;
  dps: number;
  range: number;
  structureMul: number;
};

export type DropfrontCombatDef = {
  synergyRegenPerSec: number;
  crossfireMul: number;
  coverDamageMul: number;
  barrierCoverMul: number;
  assaultMeleeMul: number;
  shooterMeleeMul: number;
  engineerNearStructFireMul: number;
  engineerNestSlowPerSec: number;
  beaconAuraRange: number;
  beaconFireMul: number;
  depotRegenPerSec: number;
  depotEnemyAuraRegen: number;
  hqPulseInterval: number;
  hqPulseDamage: number;
  hqPulseRange: number;
  turretRange: number;
  turretDps: number;
  turretFireInterval: number;
  allyMoveInterval: number;
  maxStack: number;
  enemyRoles: Record<EnemyRoleId, EnemyRoleDef>;
};

export type StructureKind = "hq" | "beacon" | "nest" | "depot" | "turret";

export type LevelStructureDef = {
  id: string;
  kind: StructureKind;
  col: number;
  row: number;
  hp: number;
  labelKey: string;
  destroyMessageKey: string;
};

export type LevelEnemyDef = {
  id: string;
  col: number;
  row: number;
  role: EnemyRoleId;
};

export type DropfrontLevelDef = {
  id: string;
  nameKey: string;
  index: number;
  cols: number;
  rows: number;
  initialSupply: number;
  maxSupply: number;
  supplyRegenPerSec: number;
  supplyDepotBonus: number;
  dropSeconds: number;
  nestSpawnInterval: number;
  nestSpawnCount: number;
  nestSpawnRoles: EnemyRoleId[];
  depotEnemyAura: boolean;
  hqPulse: boolean;
  structures: LevelStructureDef[];
  enemies: LevelEnemyDef[];
  hazardCells: { col: number; row: number }[];
};

export type DropPreviewKind =
  | "synergy"
  | "solo"
  | "stack"
  | "danger"
  | "blocked";

export type DropfrontViewDef = {
  colors: {
    bg: string;
    cell: string;
    hazardCell: string;
    gridLine: string;
    safeRing: string;
    highlightSynergy: string;
    highlightSolo: string;
    highlightStack: string;
    highlightHazard: string;
    highlightBlocked: string;
    synergyGlow: string;
    token: Record<string, string>;
    hpBar: string;
    hpBarBg: string;
    tokenStroke: string;
    tokenLabel: string;
  };
  layout: {
    topPadRatio: number;
    bottomPadRatio: number;
    boardWidthRatio: number;
    tokenSizeRatio: number;
    hpBarHeight: number;
    labelFontCellRatio: number;
    labelFontMin: number;
    landingArcCells: number;
  };
  /** Keep board under units: darken / mute tile sprites. */
  boardTiles: {
    floorTint: string;
    floorAlpha: number;
    riftTint: string;
    riftAlpha: number;
    edgeTint: string;
    edgeAlpha: number;
  };
  fx: {
    toastSec: number;
    maxDt: number;
    highlightSynergyAlpha: number;
    highlightSoloAlpha: number;
    highlightStackAlpha: number;
    highlightHazardAlpha: number;
    highlightBlockedAlpha: number;
    hazardCellAlpha: number;
    landingAlpha: number;
    synergyGlowAlpha: number;
    shakeDestroyMs: number;
    shakeDestroyIntensity: number;
    shakeFailMs: number;
    shakeFailIntensity: number;
    failFlashSec: number;
    destroySec: number;
    destroyScale: number;
  };
  rig: RigTables;
  attackFx: AttackFxTables;
};

export type DropfrontCatalog = {
  levels: readonly DropfrontLevelDef[];
  level: DropfrontLevelDef;
  cards: readonly DropfrontCardDef[];
  cardById: Record<DropfrontCardId, DropfrontCardDef>;
  combat: DropfrontCombatDef;
  view: DropfrontViewDef;
  structureById: Record<string, LevelStructureDef>;
};

const LEVEL_TABLES = [
  level01,
  level02,
  level03,
  level04,
  level05,
  level06,
  level07,
  level08,
  level09,
  level10,
] as DropfrontLevelDef[];

function rebuildStructureById(
  level: DropfrontLevelDef,
): Record<string, LevelStructureDef> {
  const structureById: Record<string, LevelStructureDef> = {};
  for (const s of level.structures) structureById[s.id] = s;
  return structureById;
}

function buildCatalog(): DropfrontCatalog {
  const levels = LEVEL_TABLES;
  const level = levels[0]!;
  const raw = cardsTable as {
    cards: DropfrontCardDef[];
    combat: DropfrontCombatDef;
  };
  const cards = raw.cards;
  const view = viewTable as DropfrontViewDef;
  const cardById = {} as Record<DropfrontCardId, DropfrontCardDef>;
  for (const c of cards) cardById[c.id] = c;
  return {
    levels,
    level,
    cards,
    cardById,
    combat: raw.combat,
    view,
    structureById: rebuildStructureById(level),
  };
}

export const dropfrontCatalog: DropfrontCatalog = buildCatalog();

export function listDropfrontLevels(): readonly DropfrontLevelDef[] {
  return dropfrontCatalog.levels;
}

export function setDropfrontLevel(id: string): DropfrontLevelDef {
  const level = dropfrontCatalog.levels.find((l) => l.id === id);
  if (!level) {
    throw new Error(`Unknown dropfront level: ${id}`);
  }
  dropfrontCatalog.level = level;
  dropfrontCatalog.structureById = rebuildStructureById(level);
  return level;
}

export function hexToColor(hex: string): number {
  const h = hex.trim().replace("#", "");
  return Number.parseInt(h.length === 3 ? h.replace(/(.)/g, "$1$1") : h, 16);
}

export function cellCenter(col: number, row: number): { x: number; y: number } {
  return { x: col + 0.5, y: row + 0.5 };
}
